export interface AnnotationVisibility {
  /** The briefer has asked to see resolved discussions, so nothing is filtered out. */
  showResolved: boolean;
  /** Comment ids from the pages of the comment list that have actually been fetched. */
  loadedCommentIds: readonly string[];
  /** Every page of the comment list has arrived, so the ids above are the whole of it. */
  commentListComplete: boolean;
}

/**
 * Which annotations belong to a discussion the briefer can still see.
 *
 * The annotations endpoint is unpaginated on purpose, so that a highlight or a count survives a
 * comment sitting on a page nobody has scrolled to. The comment list it is checked against is
 * paginated, so membership of it answers two questions at once: whether the comment is still
 * pending, and whether its page happens to have been fetched. Reading the second as the first is
 * what makes a section with a live discussion show nothing at all.
 *
 * The loaded pages are therefore read as evidence about resolution only, and only once they are
 * complete. Until then an unknown comment could be resolved or merely further down, and a
 * discussion wrongly shown is the smaller error: the badge is what tells the briefer to look, and
 * it settles as the rest of the list arrives. Resolution is not in the annotations payload, which
 * is the only reason this has to be inferred here at all.
 */
export function selectVisibleAnnotations<T extends { commentId: string }>(
  entries: readonly T[],
  { showResolved, loadedCommentIds, commentListComplete }: AnnotationVisibility,
): T[] {
  if (showResolved || !commentListComplete) return [...entries];

  const loaded = new Set(loadedCommentIds);
  return entries.filter((entry) => loaded.has(entry.commentId));
}
